import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { ExtendedRequirementTableItemData } from "../../../types";
import {
  getIsGeneralRequirement,
  sortRequirementsByPriority
} from "../../../utils";
import { useGetPayoutMethodCustomRequirement } from "./useGetPayoutMethodCustomRequirement";
import { useGetRegistrationCustomRequirement } from "./useGetRegistrationCustomRequirement";
import { useGetTaxVerificationCustomRequirement } from "./useGetTaxVerificationCustomRequirement";

type GeneralRequirements = Partial<
  Record<RequirementType, IRequirementResponse[]>
>;

export function usePayerRequirements(requirements: IRequirementResponse[]) {
  const getTaxVerificationCustomRequirement =
    useGetTaxVerificationCustomRequirement();

  const getPayoutMethodCustomRequirement =
    useGetPayoutMethodCustomRequirement();

  const getRegistrationCustomRequirement =
    useGetRegistrationCustomRequirement();

  const clientsRequirements = useMemo(() => {
    // Filter and SORT non-general requirements
    const normalizedList: ExtendedRequirementTableItemData[] =
      sortRequirementsByPriority(
        requirements.filter(
          requirement => !getIsGeneralRequirement(requirement)
        )
      );

    // Group up general requirements
    const generalRequirements = groupGeneralRequirements(requirements);

    // Build custom requirements in REVERSE order (since unshift adds to front)
    const customRequirements: ExtendedRequirementTableItemData[] = [];

    // Process in reverse order: PayoutMethod, TaxVerification, Registration
    // So that Registration ends up first after all unshifts
    if (generalRequirements[RequirementType.PayoutMethod]) {
      customRequirements.unshift(
        getPayoutMethodCustomRequirement(
          generalRequirements[RequirementType.PayoutMethod]!
        )
      );
    }
    if (generalRequirements[RequirementType.TaxVerification]) {
      customRequirements.unshift(
        getTaxVerificationCustomRequirement(
          generalRequirements[RequirementType.TaxVerification]!
        )
      );
    }
    if (generalRequirements[RequirementType.Registration]) {
      customRequirements.unshift(
        getRegistrationCustomRequirement(
          generalRequirements[RequirementType.Registration]!
        )
      );
    }

    // Combine: custom requirements first, then sorted normal requirements
    return [...customRequirements, ...normalizedList];
  }, [
    requirements,
    getTaxVerificationCustomRequirement,
    getPayoutMethodCustomRequirement,
    getRegistrationCustomRequirement
  ]);

  return clientsRequirements;
}

function groupGeneralRequirements(requirements: IRequirementResponse[]) {
  const generalRequirementMap: GeneralRequirements = {};

  requirements.forEach(requirement => {
    if (!getIsGeneralRequirement(requirement)) {
      return;
    }

    const requirementType = requirement.requirementType;
    if (!generalRequirementMap[requirementType]) {
      generalRequirementMap[requirementType] = [];
    }
    generalRequirementMap[requirementType]!.push(requirement);
  });

  return generalRequirementMap;
}
